import React, { useEffect, useRef, useState } from 'react';
import { Transition } from '@headlessui/react';
import { AiOutlineBell } from 'react-icons/ai';
import DefaultLink from '@/components/common/default/DefaultLink';
import NoDataCard from '@/components/common/NoDataCard';
import { useNotifications, useUnreadNotificationsCount } from '@/useQuery/queries/notifications';
import { ImagesPressLogosBlueSpiritorySvg } from '@/assets/images_press_logos_blue.assets';
import HeaderNotificationsDropdownItem from './HeaderNotificationsDropdownItem';
import useBackendUser from '@/hooks/useBackendUser';
import { CommonEnum } from '@/types/translations/CommonEnum';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { Notification } from '@/types/other';
import HeaderNotificationDropdownSkeleton from './HeaderNotificationDropdownSkeleton';
import { Routes } from '@/types/routes.enum';
import { useMutateNotifications } from '@/useQuery/mutations/notifications';

export default function HeaderNotificationsDropdown() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const unreadNotificationsCount = useUnreadNotificationsCount();
  const { data: user } = useBackendUser();
  const { data: notificationsQuery, isLoading: notificationsLoading } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { translate } = useTypedTranslation<CommonEnum>('common');
  const { mutateNotificationsAsReaded } = useMutateNotifications();
  // Improved outside click and focus management
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        event.button !== 2 &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!user?.canBid || !user?.canAsk || !user?.emailVerified) {
      const staticOnBoardingNotification: Notification = {
        id: 0,
        notificationType: 'onboarding',
        value: null,
        created: new Date().toLocaleDateString(),
        status: true,
        headline: translate(CommonEnum['please_complete_onboarding_process_headline']),
        subline: translate(CommonEnum['please_complete_onboarding_process_subline']),
        url: '/auth/onboarding',
        callToAc: true,
        callToAcType: '',
        imgUrl: ImagesPressLogosBlueSpiritorySvg.src,
        productName: '',
      };
      setNotifications([...(notificationsQuery || []), staticOnBoardingNotification]);
    } else {
      setNotifications(notificationsQuery || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, notificationsQuery]);

  return (
    <nav
      ref={dropdownRef}
      className="tw-relative tw-inline-block"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => {
        setIsOpen(false);
        mutateNotificationsAsReaded.mutate();
      }}
    >
      <DefaultLink
        href="/dashboard/notifications"
        className="tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-offset-2 tw-focus:ring-main/30 tw-relative tw-flex tw-cursor-pointer tw-items-center tw-rounded-xl tw-border tw-border-solid tw-border-gray-300 tw-p-2 tw-text-base tw-font-semibold tw-leading-6 tw-text-textBlack tw-shadow-lg hover:tw-text-main"
      >
        <AiOutlineBell className="tw-text-2xl" />

        {unreadNotificationsCount > 0 && (
          <div className="tw-absolute tw-right-0 tw-top-0">
            <span className="tw-flex tw-h-6 tw-w-6 tw-translate-x-1/3 tw-translate-y-[-25%] tw-transform tw-items-center tw-justify-center tw-rounded-full tw-bg-red-500 tw-text-xs tw-font-bold tw-text-white tw-shadow-sm">
              {mutateNotificationsAsReaded.isPending ? (
                <span className="tw-animate-pulse">...</span>
              ) : unreadNotificationsCount > 99 ? (
                '99+'
              ) : unreadNotificationsCount > 9 ? (
                '9+'
              ) : (
                unreadNotificationsCount
              )}
            </span>
          </div>
        )}
      </DefaultLink>

      <Transition
        show={isOpen}
        enter="tw-transition tw-ease-out tw-duration-200"
        enterFrom="tw-opacity-0 tw-scale-95"
        enterTo="tw-opacity-100 tw-scale-100"
        leave="tw-transition tw-ease-in tw-duration-150"
        leaveFrom="tw-opacity-100 tw-scale-100"
        leaveTo="tw-opacity-0 tw-scale-95"
        unmount={false}
      >
        <div
          id="header-dropdown-menu"
          role="menu"
          aria-orientation="vertical"
          className="scrollbar-none tw-max-h-100 tw-absolute tw-right-0 tw-z-[9] tw-mt-2 tw-flex tw-min-w-[500px] tw-max-w-[50vw] tw-flex-col tw-items-stretch tw-justify-center tw-gap-4 tw-overflow-hidden tw-overflow-y-scroll tw-rounded-md tw-border tw-border-solid tw-border-gray-300 tw-bg-white tw-px-4 tw-pb-4 tw-pt-6 tw-shadow-xl child:tw-flex-grow"
        >
          <div className={`tw-flex tw-items-center tw-justify-between`}>
            <div
              className={`tw-mb-2 tw-whitespace-nowrap tw-px-0 tw-text-xl tw-font-semibold tw-text-textBlack`}
            >
              {translate(CommonEnum['notifications'])}
              <hr
                aria-hidden="true"
                className="tw-absolute tw-mt-1 tw-h-[3px] tw-w-[15%] tw-rounded-full tw-border-none tw-bg-custom-gold tw-opacity-100 max-nine:tw-h-[3px]"
              />
            </div>
            <DefaultLink
              className={`tw-text-[14px] tw-text-main`}
              href={Routes._DASHBOARD_NOTIFICATIONS}
            >
              {translate(CommonEnum['view_all'])}
            </DefaultLink>
          </div>
          {notificationsLoading ? (
            <HeaderNotificationDropdownSkeleton />
          ) : notifications && notifications.length > 0 ? (
            <ul className="tw-mb-0 tw-list-none tw-space-y-4 tw-p-0">
              {notifications.slice(0, 3).map(notification => (
                <li key={notification.id} role="none">
                  <HeaderNotificationsDropdownItem item={notification} />
                </li>
              ))}
            </ul>
          ) : (
            <NoDataCard type="notifications" includeBorders={false} />
          )}
        </div>
      </Transition>
    </nav>
  );
}
